import { EraseButton } from '../components/EraseButton';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useCodeLabel, useI18n, useLocalized, useOtherLanguages } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { formatDateTime, formatPoints } from '../lib/format';
import {
  useAdminRewards, useArchiveReward, useRedemptions, useSaveReward, useUseRedemption,
} from '../lib/queries';
import { can, useSession } from '../lib/session';
import type { AdminReward } from '../lib/queries';
import {
  Alert, Button, Card, EmptyState, Field, Pill, SectionTitle, Skeleton,
} from '../components/ui';

const EMPTY = { ka: '', ru: '', en: '', pointsCost: '', validityDays: '', perCustomer: '' };

function RedeemBox() {
  const { t } = useI18n();
  const localized = useLocalized();
  const use = useUseRedemption();
  const [code, setCode] = useState('');
  const errorText = useApiError();
  const error = use.error instanceof ApiError ? use.error : null;

  return (
    <Card>
      <SectionTitle>{t('rewards.redeemCode')}</SectionTitle>
      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          use.mutate(code.trim(), { onSuccess: () => setCode('') });
        }}
      >
        <div className="min-w-0 flex-1">
          <Field
            label={t('rewards.code')}
            autoComplete="off"
            placeholder="AUD-REWARD-…"
            className="tabular uppercase"
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
          />
        </div>
        <Button type="submit" className="mb-0.5" loading={use.isPending} disabled={code.trim().length < 4}>
          {t('rewards.use')}
        </Button>
      </form>

      {error && <div className="mt-3"><Alert>{errorText(error) ?? error.message}</Alert></div>}
      {use.data && (
        <div className="mt-3">
          <Alert tone="success">
            <strong>{localized(use.data.reward)}</strong>
            <span className="mx-1.5 opacity-50">·</span>
            <Link to={`/customers/${use.data.customer.id}`} className="text-brand underline">
              {use.data.customer.name}
            </Link>{' '}
            <span className="tabular text-xs text-ink-faint">{use.data.customer.loyaltyId}</span>
          </Alert>
        </div>
      )}
    </Card>
  );
}

function RewardForm({ reward, onDone }: { reward: AdminReward | null; onDone: () => void }) {
  const { t } = useI18n();
  const save = useSaveReward();
  const [form, setForm] = useState(reward
    ? {
        ka: reward.name['ka'] ?? '', ru: reward.name['ru'] ?? '', en: reward.name['en'] ?? '',
        pointsCost: String(reward.pointsCost),
        validityDays: reward.validityDays ? String(reward.validityDays) : '',
        perCustomer: reward.redemptionLimitPerCustomer ? String(reward.redemptionLimitPerCustomer) : '',
      }
    : EMPTY);

  const errorText = useApiError();
  const error = save.error instanceof ApiError ? save.error : null;
  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const valid = form.ka && form.ru && form.en && Number(form.pointsCost) > 0;

  return (
    <Card className="mb-4">
      <SectionTitle>{reward ? t('rewards.edit') : t('rewards.new')}</SectionTitle>
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate({
            ...(reward ? { id: reward.id } : {}),
            name: { ka: form.ka.trim(), ru: form.ru.trim(), en: form.en.trim() },
            pointsCost: Number(form.pointsCost),
            validityDays: form.validityDays ? Number(form.validityDays) : null,
            redemptionLimitPerCustomer: form.perCustomer ? Number(form.perCustomer) : null,
          }, { onSuccess: onDone });
        }}
      >
        {error && <Alert>{errorText(error) ?? error.message}</Alert>}
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('rewards.nameKa')} required value={form.ka} onChange={set('ka')} />
          <Field label={t('rewards.nameRu')} required value={form.ru} onChange={set('ru')} />
          <Field label={t('rewards.nameEn')} required value={form.en} onChange={set('en')} />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field
            label={t('rewards.points')} type="number" min={1} required
            value={form.pointsCost} onChange={set('pointsCost')}
          />
          <Field
            label={t('rewards.validForDays')} type="number" min={1}
            placeholder={t('rewards.noExpiry')} value={form.validityDays} onChange={set('validityDays')}
          />
          <Field
            label={t('rewards.limitPerCustomer')} type="number" min={1}
            placeholder={t('rewards.noLimit')} value={form.perCustomer} onChange={set('perCustomer')}
          />
        </div>
        <div className="flex gap-2">
          <Button type="submit" className="flex-1" loading={save.isPending} disabled={!valid}>
            {reward ? t('common.save') : t('rewards.create')}
          </Button>
          <Button variant="ghost" type="button" onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

export function RewardsPage() {
  const { t } = useI18n();
  const localized = useLocalized();
  const otherLanguages = useOtherLanguages();
  const code = useCodeLabel();
  const { data: session } = useSession();
  const { data, isPending } = useAdminRewards();
  const archive = useArchiveReward();
  const save = useSaveReward();
  const [editing, setEditing] = useState<AdminReward | null>(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<string | undefined>('ISSUED');
  const { data: redemptions } = useRedemptions(filter);

  const mayEdit = can(session, 'rewards.update');
  const mayRedeem = can(session, 'rewards.redeem');

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">{t('rewards.title')}</h1>
        </div>
        {can(session, 'rewards.create') && (
          <Button
            variant={creating ? 'ghost' : 'primary'}
            onClick={() => { setEditing(null); setCreating((v) => !v); }}
          >
            {creating ? t('common.cancel') : t('rewards.new')}
          </Button>
        )}
      </header>

      {mayRedeem && <div className="mb-4"><RedeemBox /></div>}

      {(creating || editing) && (
        <RewardForm
          reward={editing}
          onDone={() => { setCreating(false); setEditing(null); }}
        />
      )}

      {isPending && <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-20" />)}</div>}
      {!isPending && data?.items.length === 0 && <EmptyState title={t('rewards.empty')} />}

      <div className="mb-6 space-y-2">
        {data?.items.map((reward) => (
          <Card key={reward.id} className={reward.isActive ? '' : 'opacity-60'}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-ink">{localized(reward.name)}</span>
                  {!reward.isActive && <Pill tone="warning">{t('rewards.hiddenPill')}</Pill>}
                </div>
                <div className="mt-1 text-xs text-ink-faint">
                  {otherLanguages(reward.name)}
                </div>
                <div className="mt-1 text-[0.68rem] text-ink-faint">
                  {t('rewards.issuedCount', { count: reward.issued })}
                  {reward.validityDays
                    && ` · ${t('rewards.validDays', { days: reward.validityDays })}`}
                  {reward.redemptionLimitPerCustomer
                    && ` · ${t('rewards.maxPer', { count: reward.redemptionLimitPerCustomer })}`}
                </div>
              </div>
              <div className="tabular shrink-0 text-right">
                <div className="text-base font-bold text-brand">{formatPoints(reward.pointsCost)}</div>
                <div className="text-[0.6rem] uppercase tracking-wider text-ink-faint">{t('common.pts')}</div>
              </div>
            </div>
            {/* A row under the card, not a column beside it: on a phone the column squeezed the name. */}
            {mayEdit && (
              <div className="mt-3 flex flex-wrap gap-2 border-t border-line/60 pt-3">
                <Button
                  variant="ghost" size="sm"
                  onClick={() => { setCreating(false); setEditing(reward); }}
                >{t('common.edit')}</Button>
                <Button
                  variant="quiet" size="sm"
                  onClick={() => save.mutate({ id: reward.id, isActive: !reward.isActive })}
                >
                  {reward.isActive ? t('rewards.hide') : t('rewards.show')}
                </Button>
                {reward.issued === 0 && (
                  <Button
                    variant="quiet" size="sm"
                    loading={archive.isPending}
                    onClick={() => archive.mutate(reward.id)}
                  >{t('common.remove')}</Button>
                )}
                <EraseButton compact path={`rewards/${reward.id}`} name={localized(reward.name)} />
              </div>
            )}
          </Card>
        ))}
      </div>

      <SectionTitle>{t('rewards.issuedCodes')}</SectionTitle>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {[
          { label: 'rewards.outstanding' as const, value: 'ISSUED' },
          { label: 'rewards.used' as const, value: 'USED' },
          { label: 'common.all' as const, value: undefined },
        ].map((option) => (
          <button
            key={option.label}
            onClick={() => setFilter(option.value)}
            aria-pressed={filter === option.value}
            className={`inline-flex min-h-10 items-center rounded-lg border px-3 text-xs font-medium transition-colors sm:min-h-8 sm:px-2.5 ${
              filter === option.value
                ? 'border-brand bg-brand-wash text-brand'
                : 'border-line bg-raised text-ink-dim hover:text-ink'
            }`}
          >
            {t(option.label)}
          </button>
        ))}
      </div>

      {redemptions?.items.length === 0
        ? <EmptyState title={t('rewards.nothingHere')} />
        : (
          <div className="space-y-2">
            {redemptions?.items.map((row) => (
              <Card key={row.id}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="tabular text-sm font-semibold tracking-wider text-ink">
                      {row.code}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-ink-faint">
                      {localized(row.rewardNameSnapshot)}
                      <span className="mx-1.5 opacity-50">·</span>
                      <Link to={`/customers/${row.customerId}`} className="hover:text-brand">
                        {row.customerName}
                      </Link>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <span className="tabular text-xs text-ink-dim">
                      {formatPoints(row.pointsSpent)} {t('common.pts')}
                    </span>
                    <div className="text-right">
                      <Pill tone={row.status === 'ISSUED' ? 'neutral' : 'warning'}>
                        {code('status', row.status)}
                      </Pill>
                      <div className="mt-0.5 text-[0.62rem] text-ink-faint">
                        {formatDateTime(row.usedAt ?? row.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
